import { useRef, useState } from 'react';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  applyWeboImport,
  fullWeboSelection,
  resolveWeboProgrammes,
  weboTreeItems,
} from '../../domain/importWebo';
import type { ResolvedWeboImport, WeboSelection, WeboTreeItem } from '../../domain/importWebo';
import { parseWeboXml } from '../../xml/importWebo';
import { pickFile } from '../../lib/download';
import { t } from '../../i18n';
import type { WorkspaceStore } from '../../domain/store';

function selectionTotal(sel: WeboSelection): number {
  return (sel.registrationData ? 1 : 0) + (sel.competition ? 1 : 0) + sel.starterIds.size + sel.programIds.size;
}

/**
 * Selective WeBo2008 XML import — mirrors the checkbox-tree UX of
 * `ImportExportSheet.tsx`'s "Importieren (Auswahl)…", but with WeBo's own
 * four categories instead of the six `.kuer.json` ones: Verantwortlich and
 * Wettbewerb are workspace-wide singletons (a plain checkbox each, not a
 * multi-item tree), Starter and Programme are per-`<Starter>`/`<Team>` block
 * lists. Kept as its own component rather than generalizing `TreeSheet`,
 * since that component's six-category `Selection` shape doesn't fit this
 * one's singleton + independent-pair shape without real contortion.
 */
function WeboTreeSheet({
  resolved,
  onConfirm,
  onClose,
}: {
  resolved: ResolvedWeboImport;
  onConfirm: (sel: WeboSelection) => void;
  onClose: () => void;
}) {
  const selRef = useRef<WeboSelection>(fullWeboSelection(resolved));
  const [, setVersion] = useState(0);
  const bump = (): void => setVersion((v) => v + 1);
  const sel = selRef.current;
  const { starters, programs } = weboTreeItems(resolved);
  const hasCompetition = resolved.competition !== null;
  const hasProgramme = resolved.schema === 'kunstradmeldung';

  return (
    <Sheet
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent side="bottom" className="gap-0 p-0">
        <SheetHeader>
          <SheetTitle>{t('webo.title')}</SheetTitle>
        </SheetHeader>
        <SheetBody className="flex flex-col gap-4 p-4">
          {resolved.warnings.length > 0 ? (
            <Alert>
              <AlertDescription>
                <p className="mb-1 font-medium">{t('webo.warnings.title', { count: resolved.warnings.length })}</p>
                <ul className="list-disc pl-4">
                  {resolved.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : null}

          <label className="flex items-center gap-2.5 py-1 text-sm">
            <Checkbox
              checked={sel.registrationData}
              disabled={resolved.contact === null}
              onCheckedChange={(checked) => {
                sel.registrationData = checked === true;
                bump();
              }}
            />
            <span className="flex flex-col">
              <span className="font-medium">{t('webo.category.registrationData')}</span>
              <span className="text-xs text-muted-foreground">{t('webo.category.registrationData.hint')}</span>
            </span>
          </label>

          <label className="flex items-center gap-2.5 py-1 text-sm">
            <Checkbox
              checked={sel.competition}
              disabled={!hasCompetition}
              onCheckedChange={(checked) => {
                sel.competition = checked === true;
                bump();
              }}
            />
            <span className="flex flex-col">
              <span className="font-medium">{t('webo.category.wettbewerb')}</span>
              {!hasCompetition ? <span className="text-xs text-muted-foreground">{t('webo.category.wettbewerb.unavailable')}</span> : null}
            </span>
          </label>

          <WeboSection
            title={t('webo.category.starter')}
            items={starters}
            ids={sel.starterIds}
            onBump={bump}
          />

          {hasProgramme ? (
            <WeboSection title={t('webo.category.programme')} items={programs} ids={sel.programIds} onBump={bump} />
          ) : (
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">{t('webo.category.programme')}: </span>
              {t('webo.category.programme.unavailable')}
            </p>
          )}
        </SheetBody>
        <SheetFooter>
          <span className="flex-1" />
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              onConfirm(sel);
              onClose();
            }}
          >
            {t('ie.confirm.import')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function WeboSection({ title, items, ids, onBump }: { title: string; items: readonly WeboTreeItem[]; ids: Set<string>; onBump: () => void }) {
  const state: 'checked' | 'indeterminate' | 'unchecked' =
    items.length === 0 || ids.size === 0 ? 'unchecked' : ids.size === items.length ? 'checked' : 'indeterminate';

  return (
    <div>
      <label className="flex items-center gap-2.5 border-b py-1 pb-1.5 font-medium">
        <Checkbox
          checked={state === 'indeterminate' ? 'indeterminate' : state === 'checked'}
          disabled={items.length === 0}
          onCheckedChange={(checked) => {
            ids.clear();
            if (checked === true) for (const item of items) ids.add(item.id);
            onBump();
          }}
        />
        <span className="flex items-center gap-2 text-sm">
          {title} <Badge>{items.length || t('ie.section.empty')}</Badge>
        </span>
      </label>
      {items.length > 0 ? (
        <div className="flex flex-col gap-0.5 pt-1.5 pl-7">
          {items.map((item) => (
            <label className={`flex items-center gap-2.5 py-1 text-sm ${item.mismatch ? 'text-destructive' : 'text-muted-foreground'}`} key={item.id}>
              <Checkbox
                checked={ids.has(item.id)}
                onCheckedChange={(checked) => {
                  if (checked === true) ids.add(item.id);
                  else ids.delete(item.id);
                  onBump();
                }}
              />
              <span>{item.label}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export type WeboImportStage = { kind: 'closed' } | { kind: 'tree'; resolved: ResolvedWeboImport };

export function WeboImportSheet({
  store,
  toast,
  stage,
  onClose,
}: {
  store: WorkspaceStore;
  toast: (message: string) => void;
  stage: WeboImportStage;
  onClose: () => void;
}) {
  function handleConfirm(resolved: ResolvedWeboImport, sel: WeboSelection): void {
    if (selectionTotal(sel) === 0) {
      toast(t('webo.import.noneSelected'));
      return;
    }
    const result = applyWeboImport(store.workspace, resolved, sel);
    store.replace(result.workspace);
    toast(
      t('webo.import.done', {
        comps: result.created.comps,
        starters: result.created.starters,
        athletesNew: result.created.athletes,
        athletesReused: result.reused.athletes,
        athletesFilled: result.updated.athletes,
        clubsNew: result.created.clubs,
        programs: result.created.programs,
        entries: result.created.entries,
      }),
    );
  }

  if (stage.kind !== 'tree') return null;
  return <WeboTreeSheet resolved={stage.resolved} onConfirm={(sel) => handleConfirm(stage.resolved, sel)} onClose={onClose} />;
}

/** Kicks off the async file-pick + parse + catalogue resolve for
 *  "WeBo-Import…" — belongs in a click handler (not an effect), same
 *  reasoning as `pickImportFile()` in `ImportExportSheet.tsx`. */
export async function pickWeboImportFile(toast: (message: string) => void): Promise<WeboImportStage | null> {
  const file = await pickFile('application/xml,text/xml,.xml');
  if (!file) return null;
  try {
    const imp = parseWeboXml(file.text);
    const resolved = await resolveWeboProgrammes(imp);
    return { kind: 'tree', resolved };
  } catch {
    toast(t('webo.import.invalid'));
    return null;
  }
}
